import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Store, Search, ExternalLink, Power, Pencil, Trash2, Mail, Package as PackageIcon, Star, ShieldCheck, RefreshCw } from 'lucide-react';

const StatCard = ({ label, value, icon: Icon }) => (
    <Card>
        <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center">
                <Icon className="h-5 w-5 text-[hsl(var(--primary))]" />
            </div>
            <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className="text-xl font-semibold">{value ?? '—'}</div>
            </div>
        </CardContent>
    </Card>
);

export default function AdminStorefront() {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('all');
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null); // detail dialog
    const [editing, setEditing] = useState(null);   // storefront config editing
    const [savingEdit, setSavingEdit] = useState(false);
    const [tab, setTab] = useState('branding');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: list }, { data: st }] = await Promise.all([
                api.get('/admin/storefronts', { params: { q, status, limit: 100 } }),
                api.get('/admin/storefronts/stats'),
            ]);
            setItems(list?.items || []);
            setStats(st || null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al cargar tiendas');
        } finally {
            setLoading(false);
        }
    }, [q, status]);

    useEffect(() => { load(); }, [load]);

    const openDetail = async (userId) => {
        try {
            const { data } = await api.get(`/admin/storefronts/${userId}`);
            setSelected(data);
            setEditing({ ...data.storefront });
            setTab('branding');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error');
        }
    };

    const toggleEnabled = async (userId, nickname) => {
        try {
            const { data } = await api.post(`/admin/storefronts/${userId}/toggle`);
            toast.success(`Tienda de @${nickname} ${data.enabled ? 'habilitada' : 'deshabilitada'}`);
            load();
            if (selected?.user?.id === userId) {
                setSelected(prev => ({ ...prev, storefront: { ...prev.storefront, enabled: data.enabled } }));
                setEditing(prev => ({ ...prev, enabled: data.enabled }));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al alternar');
        }
    };

    const saveEdit = async () => {
        if (!selected || !editing) return;
        setSavingEdit(true);
        try {
            const payload = { ...editing };
            if (!payload.socials) payload.socials = {};
            if (!payload.theme) payload.theme = 'minimal';
            const { data } = await api.put(`/admin/storefronts/${selected.user.id}`, payload);
            toast.success('Cambios guardados');
            setSelected(prev => ({ ...prev, storefront: data.storefront }));
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        } finally {
            setSavingEdit(false);
        }
    };

    const deleteMessage = async (msgId) => {
        if (!selected) return;
        if (!window.confirm('¿Eliminar este mensaje?')) return;
        try {
            await api.delete(`/admin/storefronts/${selected.user.id}/messages/${msgId}`);
            toast.success('Mensaje eliminado');
            setSelected(prev => ({ ...prev, messages: prev.messages.filter(m => m.id !== msgId) }));
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const markRead = async (msgId) => {
        if (!selected) return;
        try {
            await api.post(`/admin/storefronts/${selected.user.id}/messages/${msgId}/read`);
            setSelected(prev => ({ ...prev, messages: prev.messages.map(m => m.id === msgId ? { ...m, read: true } : m) }));
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const deleteCollection = async (colId) => {
        if (!selected) return;
        if (!window.confirm('¿Eliminar esta colección?')) return;
        try {
            await api.delete(`/admin/storefronts/${selected.user.id}/collections/${colId}`);
            toast.success('Colección eliminada');
            setSelected(prev => ({ ...prev, collections: prev.collections.filter(c => c.id !== colId) }));
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const closeDetail = () => { setSelected(null); setEditing(null); };

    return (
        <div className="space-y-6" data-testid="admin-storefront-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2">
                        <Store className="h-6 w-6 text-[hsl(var(--primary))]" />
                        <h1 className="text-2xl font-semibold">OkStore — Tiendas individuales</h1>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                        Modera, edita branding y administra las tiendas Shopify-lite de los vendedores.
                    </p>
                </div>
                <Button variant="outline" onClick={load} data-testid="okstore-refresh">
                    <RefreshCw className="h-4 w-4 mr-2" />Actualizar
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard label="Usuarios" value={stats?.total_users} icon={PackageIcon} />
                <StatCard label="Tiendas activas" value={stats?.enabled_storefronts} icon={Store} />
                <StatCard label="Colecciones" value={stats?.total_collections} icon={PackageIcon} />
                <StatCard label="Mensajes" value={stats?.total_messages} icon={Mail} />
                <StatCard label="Sin leer" value={stats?.unread_messages} icon={Mail} />
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[240px]">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Buscar por nickname, email o nombre"
                            className="pl-9"
                            data-testid="okstore-search"
                        />
                    </div>
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="w-[200px]" data-testid="okstore-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todas</SelectItem>
                            <SelectItem value="enabled">Habilitadas</SelectItem>
                            <SelectItem value="disabled">Deshabilitadas</SelectItem>
                        </SelectContent>
                    </Select>
                </CardContent>
            </Card>

            {/* List */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {loading && Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-40 rounded-xl" />
                ))}
                {!loading && items.length === 0 && (
                    <div className="col-span-full text-sm text-muted-foreground py-16 text-center">
                        No hay tiendas que coincidan con el filtro.
                    </div>
                )}
                {!loading && items.map(it => (
                    <Card key={it.user_id} data-testid={`okstore-card-${it.nickname}`} className="overflow-hidden">
                        <div className="h-20 w-full bg-gradient-to-br from-[hsl(var(--accent))] to-[hsl(var(--secondary))] relative">
                            {it.banner_url && <img src={it.banner_url} alt="" className="absolute inset-0 h-full w-full object-cover" />}
                            <div className="absolute top-2 right-2">
                                <Badge className={it.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}>
                                    {it.enabled ? 'Habilitada' : 'Deshabilitada'}
                                </Badge>
                            </div>
                        </div>
                        <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                                <div className="h-12 w-12 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center overflow-hidden -mt-8 border-4 border-card">
                                    {it.logo_url
                                        ? <img src={it.logo_url} alt="" className="h-full w-full object-cover" />
                                        : <span className="font-semibold">{it.nickname?.[0]?.toUpperCase()}</span>}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                        <div className="font-semibold truncate">@{it.nickname}</div>
                                        {it.verified && <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">
                                        {it.display_name || it.full_name || '—'}
                                    </div>
                                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
                                        <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3" />{(it.rating_avg || 0).toFixed(1)}</span>
                                        <span>·</span>
                                        <span>{it.ads_active} anuncios</span>
                                        <span>·</span>
                                        <span>{it.collections_count} colecc.</span>
                                    </div>
                                </div>
                            </div>
                            {it.tagline && <div className="text-xs text-muted-foreground mt-3 line-clamp-2">{it.tagline}</div>}
                            <div className="flex gap-2 mt-4">
                                <Button size="sm" variant="outline" className="flex-1" onClick={() => openDetail(it.user_id)} data-testid={`okstore-edit-${it.nickname}`}>
                                    <Pencil className="h-3.5 w-3.5 mr-1" />Administrar
                                </Button>
                                <Button size="sm" variant={it.enabled ? 'outline' : 'default'} onClick={() => toggleEnabled(it.user_id, it.nickname)} data-testid={`okstore-toggle-${it.nickname}`}>
                                    <Power className="h-3.5 w-3.5" />
                                </Button>
                                <Link to={`/store/${it.nickname}`} target="_blank">
                                    <Button size="sm" variant="ghost" data-testid={`okstore-view-${it.nickname}`}>
                                        <ExternalLink className="h-3.5 w-3.5" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Detail Dialog */}
            <Dialog open={!!selected} onOpenChange={(v) => !v && closeDetail()}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="okstore-detail-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Store className="h-5 w-5" />
                            @{selected?.user?.nickname} — Administración de OkStore
                        </DialogTitle>
                    </DialogHeader>
                    {selected && editing && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--secondary))]">
                                <div>
                                    <div className="text-sm font-medium">Estado de la tienda</div>
                                    <div className="text-xs text-muted-foreground">
                                        {editing.enabled ? 'Visible públicamente en /store/' + selected.user.nickname : 'Oculta al público'}
                                    </div>
                                </div>
                                <Switch
                                    checked={!!editing.enabled}
                                    onCheckedChange={() => toggleEnabled(selected.user.id, selected.user.nickname)}
                                    data-testid="okstore-detail-toggle"
                                />
                            </div>

                            <Tabs value={tab} onValueChange={setTab}>
                                <TabsList>
                                    <TabsTrigger value="branding" data-testid="okstore-tab-branding">Branding</TabsTrigger>
                                    <TabsTrigger value="policies" data-testid="okstore-tab-policies">Políticas</TabsTrigger>
                                    <TabsTrigger value="collections" data-testid="okstore-tab-collections">
                                        Colecciones ({selected.collections?.length || 0})
                                    </TabsTrigger>
                                    <TabsTrigger value="messages" data-testid="okstore-tab-messages">
                                        Mensajes ({selected.messages?.length || 0})
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent value="branding" className="space-y-3 pt-4">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label>Nombre público</Label>
                                            <Input value={editing.display_name || ''} onChange={(e) => setEditing({ ...editing, display_name: e.target.value })} data-testid="okstore-edit-name" />
                                        </div>
                                        <div>
                                            <Label>Tema</Label>
                                            <Select value={editing.theme || 'minimal'} onValueChange={(v) => setEditing({ ...editing, theme: v })}>
                                                <SelectTrigger data-testid="okstore-edit-theme"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="minimal">Minimal</SelectItem>
                                                    <SelectItem value="bold">Bold</SelectItem>
                                                    <SelectItem value="editorial">Editorial</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div>
                                        <Label>Tagline</Label>
                                        <Input value={editing.tagline || ''} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} data-testid="okstore-edit-tagline" />
                                    </div>
                                    <div>
                                        <Label>Bio extendida</Label>
                                        <Textarea rows={3} value={editing.bio_extended || ''} onChange={(e) => setEditing({ ...editing, bio_extended: e.target.value })} data-testid="okstore-edit-bio" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label>Logo URL</Label>
                                            <Input value={editing.logo_url || ''} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} data-testid="okstore-edit-logo" />
                                        </div>
                                        <div>
                                            <Label>Banner URL</Label>
                                            <Input value={editing.banner_url || ''} onChange={(e) => setEditing({ ...editing, banner_url: e.target.value })} data-testid="okstore-edit-banner" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label>Color primario</Label>
                                            <Input value={editing.primary_color || ''} onChange={(e) => setEditing({ ...editing, primary_color: e.target.value })} placeholder="#dc2626" />
                                        </div>
                                        <div>
                                            <Label>Color acento</Label>
                                            <Input value={editing.accent_color || ''} onChange={(e) => setEditing({ ...editing, accent_color: e.target.value })} placeholder="#fef3c7" />
                                        </div>
                                    </div>
                                </TabsContent>

                                <TabsContent value="policies" className="space-y-3 pt-4">
                                    <div>
                                        <Label>Envíos</Label>
                                        <Textarea rows={3} value={editing.policies_shipping || ''} onChange={(e) => setEditing({ ...editing, policies_shipping: e.target.value })} />
                                    </div>
                                    <div>
                                        <Label>Devoluciones</Label>
                                        <Textarea rows={3} value={editing.policies_returns || ''} onChange={(e) => setEditing({ ...editing, policies_returns: e.target.value })} />
                                    </div>
                                    <div>
                                        <Label>Privacidad</Label>
                                        <Textarea rows={3} value={editing.policies_privacy || ''} onChange={(e) => setEditing({ ...editing, policies_privacy: e.target.value })} />
                                    </div>
                                </TabsContent>

                                <TabsContent value="collections" className="space-y-2 pt-4">
                                    {selected.collections?.length === 0 && (
                                        <div className="text-sm text-muted-foreground py-6 text-center">Este vendedor aún no tiene colecciones.</div>
                                    )}
                                    {selected.collections?.map(col => (
                                        <div key={col.id} className="flex items-center justify-between p-3 rounded-lg border">
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium truncate">{col.name}</div>
                                                <div className="text-xs text-muted-foreground truncate">/{col.slug} · {col.ad_ids?.length || 0} anuncios · {col.active ? 'Activa' : 'Inactiva'}</div>
                                            </div>
                                            <Button size="sm" variant="ghost" onClick={() => deleteCollection(col.id)} data-testid={`okstore-del-col-${col.id}`}>
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        </div>
                                    ))}
                                </TabsContent>

                                <TabsContent value="messages" className="space-y-2 pt-4">
                                    {selected.messages?.length === 0 && (
                                        <div className="text-sm text-muted-foreground py-6 text-center">Sin mensajes de contacto.</div>
                                    )}
                                    {selected.messages?.map(m => (
                                        <div key={m.id} className={`p-3 rounded-lg border ${m.read ? 'opacity-70' : 'bg-[hsl(var(--secondary))]/40'}`}>
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium">{m.name} <span className="text-xs text-muted-foreground">&lt;{m.email}&gt;</span></div>
                                                    {m.subject && <div className="text-xs font-medium text-foreground mt-0.5">{m.subject}</div>}
                                                    <div className="text-sm mt-1 whitespace-pre-wrap">{m.message}</div>
                                                    <div className="text-[10px] text-muted-foreground mt-1">{m.created_at}</div>
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    {!m.read && (
                                                        <Button size="sm" variant="outline" onClick={() => markRead(m.id)} data-testid={`okstore-msg-read-${m.id}`}>Leído</Button>
                                                    )}
                                                    <Button size="sm" variant="ghost" onClick={() => deleteMessage(m.id)} data-testid={`okstore-msg-del-${m.id}`}>
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </TabsContent>
                            </Tabs>
                        </div>
                    )}
                    <DialogFooter>
                        <Link to={selected ? `/store/${selected.user.nickname}` : '#'} target="_blank">
                            <Button variant="outline" data-testid="okstore-view-public">
                                <ExternalLink className="h-4 w-4 mr-1" />Ver tienda pública
                            </Button>
                        </Link>
                        <Button variant="ghost" onClick={closeDetail}>Cerrar</Button>
                        <Button onClick={saveEdit} disabled={savingEdit} data-testid="okstore-save">
                            {savingEdit ? 'Guardando…' : 'Guardar cambios'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
